import { getProductSections } from "../const";
import classNames from "classnames";
import React from "react";
import TrendUpIcon from "../icons/headerExploreIcons/trend-up.svg";
import WalletIcon from "../icons/headerExploreIcons/wallet.svg";
import HandIcon from "../icons/headerExploreIcons/hand.svg";
import FeesIcon from "../icons/headerExploreIcons/fees2.svg";
import CareerIcon from "../icons/headerExploreIcons/career.svg";
import AboutIcon from "../icons/headerExploreIcons/BF1.svg";
import ChevronDownBlackIcon from "../icons/chevron-down-black.svg";
import { rootUrl, WORDPRESS_URL } from "@bundles/consts";

type DropdownSectionLink = {
  label: string;
  url?: string;
  onClick?: () => void;
  icon?: React.FC<React.SVGProps<SVGSVGElement>> | string;
};

type DopdownSection = {
  title?: string;
  titleBackground?: string;
  links: DropdownSectionLink[];
};

type DropdownProps = {
  boxTitle?: string;
  boxDescription?: string;
  boxBtnText?: string;
  boxBtnUrl?: string;
  sections: DopdownSection[];
  exploreUrl?: string;
  isMobileView?: boolean;
  isExplore?: boolean;
};

export const getExploreSections = (
  isUk?: boolean,
  isUkRestricted?: boolean
) => {
  const sections = [
    {
      links: [
        {
          label: "Private Equity Markets",
          url: "/market/spvs",
          icon: WalletIcon,
        },
        {
          label: "Portfolio Company Directory",
          url: "/companies",
          icon: HandIcon,
        },
      ],
    },
    {
      links: [
        {
          label: "Fees",
          url: `${WORDPRESS_URL}/fees`,
          icon: FeesIcon,
        },
        {
          label: "Careers",
          url: `${WORDPRESS_URL}/careers`,
          icon: CareerIcon,
        },
        {
          label: "About us",
          url: `${WORDPRESS_URL}/about`,
          icon: AboutIcon,
        },
      ],
    },
  ];

  const ukRestrictedSections = [
    {
      links: [
        {
          label: "Fees",
          url: `${WORDPRESS_URL}/fees`,
          icon: FeesIcon,
        },
        {
          label: "Careers",
          url: `${WORDPRESS_URL}/careers`,
          icon: CareerIcon,
        },
      ],
    },
    {
      links: [
        {
          label: "About us",
          url: `${WORDPRESS_URL}/about`,
          icon: AboutIcon,
        },
      ],
    },
  ];

  return isUkRestricted ? ukRestrictedSections : sections;
};

const Dropdown: React.FC<DropdownProps> = ({
  sections,
  exploreUrl,
  isMobileView,
  isExplore,
}) => (
  <div
    className={classNames("flex flex-col bg-white text-typo-dark", {
      "lg:flex-row p-4 gap-4 lg:p-8 lg:gap-8 p-8 gap-8": !isMobileView,
      "flex-col": isMobileView,
    })}
  >
    <div
      className={classNames("flex flex-col gap-[60px] ", {
        "lg:flex-row lg:flex-1 lg:gap-[60px] lg:justify-between": !isMobileView,
        "flex-col gap-4": isMobileView,
      })}
    >
      {sections.map((section, idx) => (
        <div
          key={`${section.title}-${idx}`}
          className={classNames("whitespace-nowrap", {
            "mb-4 lg:mb-0": !isMobileView,
            "mb-4": isMobileView && !isExplore,
            "mb-0": isExplore,
          })}
        >
          {!!section.title && (
            <div
              className={classNames(
                "text-white py-1 px-2 rounded-[6px] inline-block text-[13px] uppercase",
                section.titleBackground,
                {
                  "mb-[28px] min-h-6": !isMobileView,
                  "mb-4": isMobileView,
                }
              )}
            >
              {section.title}
            </div>
          )}

          <div className="flex flex-col">
            {section.links.map((sectionLink, linkIdx) => {
              const handleClick = () => {
                if (sectionLink.onClick) {
                  sectionLink.onClick();
                } else if (sectionLink.url) {
                  const isExternal = sectionLink.url.includes("/bwb");

                  if (isExternal) {
                    window.open(sectionLink.url, "_blank");
                  } else {
                    window.location.href = sectionLink.url;
                  }
                }
              };

              return (
                <div
                  key={`sectionLink-${linkIdx}`}
                  onClick={handleClick}
                  className="flex items-center gap-4 cursor-pointer hover:bg-[#F6F6F6] hover:rounded-[10px] py-[11px]"
                >
                  {!!sectionLink.icon &&
                    !isMobileView &&
                    (typeof sectionLink.icon === "string" ? (
                      <img
                        src={sectionLink.icon}
                        width={32}
                        height={32}
                        alt=""
                      />
                    ) : (
                      <sectionLink.icon className="text-primary-dark" />
                    ))}
                  <div
                    className={classNames({
                      "pl-[12px]": isMobileView,
                    })}
                  >
                    {sectionLink.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!isMobileView && (
        <div
          className={classNames("max-w-[300px] mt-2", {
            "lg:mt-0": !isMobileView,
          })}
        >
          <div className="uppercase text-primary-dark text-[13px] rounded-[6px] bg-[#F1EEF6] inline-block px-2 py-1">
            featured investment
          </div>
          <div className={classNames("my-3", { "lg:my-5": !isMobileView })}>
            Discover how our Bnk To The Future ETH Staking Solution enables you
            to earn weekly ETH, Bitcoin or USD income.
          </div>
          <a
            className="border-b border-[#323232] inline-flex items-center font-semibold"
            href={exploreUrl}
          >
            Explore
            <ChevronDownBlackIcon
              width={12}
              height={12}
              className="rotate-[-90deg] ml-1"
            />
          </a>
        </div>
      )}
    </div>
  </div>
);

type DropdownWrapperProps = {
  isUk?: boolean;
  userSignedIn?: boolean;
  slug?: string;
  isMobileView?: boolean;
};

export const ProductsDropdown = ({
  isUk,
  slug,
  isMobileView,
}: DropdownWrapperProps) => {
  const productsSections = getProductSections(slug, isUk);
  const exploreUrl = `${rootUrl}/profile/${slug}/funds/stakingEth`;

  return (
    <Dropdown
      exploreUrl={exploreUrl}
      sections={productsSections}
      isMobileView={isMobileView}
    />
  );
};

export const ExploreDropdown = ({
  isUk,
  userSignedIn,
  slug,
  isMobileView,
}: DropdownWrapperProps) => {
  const exploreUrl = `${rootUrl}/profile/${slug}/funds/stakingEth`;

  const exploreSections = getExploreSections(isUk, !userSignedIn && isUk);

  return (
    <Dropdown
      sections={exploreSections}
      isExplore
      exploreUrl={exploreUrl}
      isMobileView={isMobileView}
    />
  );
};
